/* Separate Combo and regular orders in this theme. No cart items are removed.
 * Shopify checkout itself needs a server validation to enforce this outside
 * the theme (direct checkout URLs, other sales channels or disabled scripts).
 */
(() => {
  'use strict';
  const configElement = document.getElementById('amigos-combo-cart-config');
  if (!configElement || window.AmigosComboCart) return;
  const config = JSON.parse(configElement.textContent);
  const nativeFetch = window.fetch.bind(window);
  const root = config.root.endsWith('/') ? config.root : config.root + '/';
  const comboVariants = new Set();
  const comboProducts = new Set();
  const comboHandles = new Set();
  const comboTitles = new Set();
  const messages = {
    combo: 'Du har en Combo i handlekurven. Andre produkter må bestilles separat. Fjern Combo-produktene først hvis du vil bestille fra den vanlige menyen.',
    regular: 'Du har andre produkter i handlekurven. Combos må bestilles separat. Fjern de andre produktene først hvis du vil bestille en Combo.',
    mixed: 'Combos må bestilles separat. Fjern enten Combo-produktene eller de andre produktene fra handlekurven før du går til kassen.',
    error: 'Vi kunne ikke kontrollere handlekurven. Prøv igjen før du legger til produkter eller går til kassen.'
  };
  let cataloguePromise;
  let mutationQueue = Promise.resolve();
  let latestCart;
  let currentMode = 'empty';
  const allowedForms = new WeakSet();
  const busyForms = new WeakSet();
  const allowedClicks = new WeakSet();

  function register(product) {
    if (!product || product.type !== 'combo') return;
    comboProducts.add(String(product.productId));
    if (product.handle) comboHandles.add(product.handle);
    if (product.title) comboTitles.add(product.title);
    (product.variantIds || []).forEach(id => comboVariants.add(String(id)));
  }
  [...config.products, ...config.combos, config.product].forEach(register);

  function registerForms() {
    document.querySelectorAll('[data-amigos-product-type="combo"]').forEach(element => {
      try {
        register({type: 'combo', productId: element.dataset.amigosProductId,
          variantIds: JSON.parse(element.dataset.amigosVariantIds || '[]')});
      } catch (_) { /* A malformed optional form annotation is ignored. */ }
    });
  }

  async function getJSON(url) {
    const response = await nativeFetch(url, {cache: 'no-store', credentials: 'same-origin',
      headers: {Accept: 'application/json'}});
    if (!response.ok) throw new Error('Cart lookup failed');
    return response.json();
  }

  async function loadCatalogue() {
    registerForms();
    if (!cataloguePromise) {
      cataloguePromise = (async () => {
        // Every page is read, so future Combo products beyond the initial
        // Liquid list also work. No IDs or prices are hard-coded.
        for (let page = 1; ; page++) {
          const data = await getJSON(root + 'collections/' + encodeURIComponent(config.collectionHandle) +
            '/products.json?limit=250&page=' + page);
          if (!Array.isArray(data.products)) throw new Error('Invalid catalogue');
          data.products.forEach(product => register({type: 'combo', productId: product.id,
            handle: product.handle, title: product.title,
            variantIds: product.variants.map(variant => variant.id)}));
          if (data.products.length < 250) break;
        }
      })().catch(error => { cataloguePromise = undefined; throw error; });
    }
    return cataloguePromise;
  }

  function isCombo(item) {
    return comboProducts.has(String(item.product_id)) || comboVariants.has(String(item.variant_id || item.id)) ||
      comboHandles.has(item.handle) || comboTitles.has(item.product_title);
  }

  function cartMode(items) {
    const modes = new Set(items.filter(item => Number(item.quantity) > 0).map(item => isCombo(item) ? 'combo' : 'regular'));
    return modes.size > 1 ? 'mixed' : (modes.values().next().value || 'empty');
  }

  function applyCheckoutState() {
    const blocked = currentMode === 'mixed' || currentMode === 'error';
    document.documentElement.dataset.amigosCartMode = currentMode;
    document.querySelectorAll('button[name="checkout"]').forEach(button => {
      if (blocked && !button.disabled) {
        button.dataset.amigosComboDisabled = 'true';
        button.disabled = true;
      } else if (!blocked && button.dataset.amigosComboDisabled === 'true') {
        button.disabled = !latestCart || latestCart.item_count === 0;
        delete button.dataset.amigosComboDisabled;
      }
    });
    let warning = document.getElementById('amigos-combo-cart-warning');
    const main = document.getElementById('MainContent');
    if (!warning && blocked && main) {
      warning = document.createElement('div');
      warning.id = 'amigos-combo-cart-warning';
      warning.className = 'amigos-combo-cart-warning';
      warning.setAttribute('role', 'alert');
      main.prepend(warning);
    }
    if (warning) {
      warning.hidden = !blocked;
      const text = messages[currentMode] || '';
      if (warning.textContent !== text) warning.textContent = text;
    }
  }

  async function readCart() {
    await loadCatalogue();
    const cart = await getJSON(root + 'cart.js');
    if (!Array.isArray(cart.items)) throw new Error('Invalid cart');
    latestCart = cart;
    currentMode = cartMode(cart.items);
    applyCheckoutState();
    return cart;
  }

  function showMessage(message) {
    if (!document.body) {
      document.addEventListener('DOMContentLoaded', () => showMessage(message), {once: true});
      return;
    }
    let box = document.getElementById('amigos-combo-cart-message');
    if (!box) {
      box = document.createElement('div');
      box.id = 'amigos-combo-cart-message';
      box.className = 'amigos-combo-cart-message';
      box.setAttribute('role', 'alert');
      const close = document.createElement('button');
      close.type = 'button';
      close.className = 'amigos-combo-cart-close';
      close.setAttribute('aria-label', 'Lukk melding');
      close.textContent = '×';
      close.addEventListener('click', () => { box.hidden = true; });
      box.append(close, document.createElement('p'));
      const link = document.createElement('a');
      link.href = config.cartUrl;
      link.textContent = 'Åpne handlekurven';
      box.append(link);
      document.body.append(box);
    }
    box.querySelector('p').textContent = message;
    box.hidden = false;
  }

  function failure(message) {
    showMessage(message);
    return {status: 422, message, description: message};
  }

  function serial(operation) {
    const result = mutationQueue.then(operation);
    mutationQueue = result.catch(() => {});
    return result;
  }

  function endpoint(input) {
    const value = input instanceof Request ? input.url : input;
    const url = new URL(value, window.location.href);
    if (url.origin !== window.location.origin) return null;
    const match = url.pathname.match(/\/cart\/(add|change|update|clear)(?:\.js|\.json)?\/?$/);
    return match ? match[1] : null;
  }

  function parseFields(fields) {
    const object = {items: [], updates: {}};
    const indexed = new Map();
    for (const [key, value] of fields.entries()) {
      let match = key.match(/^items\[(\d+)\]\[(id|quantity|parent_id)\]$/);
      if (match) {
        const item = indexed.get(match[1]) || {};
        item[match[2]] = value;
        indexed.set(match[1], item);
      } else if ((match = key.match(/^updates\[(.+)\]$/))) {
        object.updates[match[1]] = value;
      } else if (key === 'id' || key === 'quantity' || key === 'parent_id') {
        object[key] = value;
      }
    }
    object.items = [...indexed.values()];
    return object;
  }

  async function requestPayload(input, init) {
    let body = init && init.body;
    if (body === undefined && input instanceof Request) {
      const copy = input.clone();
      body = (copy.headers.get('content-type') || '').includes('multipart/form-data') ?
        await copy.formData() : await copy.text();
    }
    if (body instanceof FormData || body instanceof URLSearchParams) return parseFields(body);
    if (typeof body === 'string') {
      try { return JSON.parse(body); } catch (_) { return parseFields(new URLSearchParams(body)); }
    }
    if (body === undefined || body === null) {
      const url = new URL(input instanceof Request ? input.url : input, window.location.href);
      return parseFields(url.searchParams);
    }
    throw new Error('Unsupported add request');
  }

  function additions(payload, action, cart) {
    if (action === 'add') {
      return (payload.items && payload.items.length ? payload.items : [payload])
        .filter(item => item.id && Number(item.quantity ?? 1) > 0);
    }
    if (action === 'update' && payload.updates && !Array.isArray(payload.updates)) {
      return Object.entries(payload.updates).filter(([id, quantity]) => Number(quantity) > 0 &&
        !cart.items.some(item => String(item.variant_id) === id || item.key === id))
        .map(([id, quantity]) => ({id, quantity}));
    }
    return [];
  }

  async function validate(payload, action) {
    // Change/remove/clear requests must remain usable to repair old mixed carts.
    if (action === 'change' || action === 'clear') return null;
    const cart = await readCart();
    const incoming = additions(payload, action, cart);
    if (!incoming.length) return null;
    const modes = new Set(incoming.map(item => comboVariants.has(String(item.id)) ? 'combo' : 'regular'));
    if (modes.size > 1 || currentMode === 'mixed') return messages.mixed;
    const requestedMode = modes.values().next().value;
    if (currentMode !== 'empty' && currentMode !== requestedMode) return messages[currentMode];
    return null;
  }

  async function afterMutation() {
    try { await readCart(); } catch (_) { currentMode = 'error'; applyCheckoutState(); }
  }

  // Intercept the theme's and apps' Ajax cart writes before sending them.
  // Allowed requests are forwarded untouched, retaining all option properties.
  window.fetch = function(input, init) {
    const action = endpoint(input);
    if (!action) return nativeFetch(input, init);
    return serial(async () => {
      let message;
      try { message = await validate(await requestPayload(input, init), action); }
      catch (_) { message = messages.error; }
      if (message) return new Response(JSON.stringify(failure(message)), {
        status: 422, headers: {'Content-Type': 'application/json'}
      });
      const response = await nativeFetch(input, init);
      if (response.ok) await afterMutation();
      return response;
    });
  };

  // Option apps may use XMLHttpRequest/jQuery rather than fetch.
  const xhrOpen = XMLHttpRequest.prototype.open;
  const xhrSend = XMLHttpRequest.prototype.send;
  const xhrAbort = XMLHttpRequest.prototype.abort;
  const xhrStates = new WeakMap();
  const syntheticKeys = ['readyState', 'status', 'statusText', 'response', 'responseText'];
  XMLHttpRequest.prototype.open = function(method, url, async = true, ...rest) {
    const old = xhrStates.get(this);
    if (old && old.synthetic) syntheticKeys.forEach(key => { delete this[key]; });
    xhrStates.set(this, {action: endpoint(url), url, method, async, aborted: false});
    return xhrOpen.call(this, method, url, async, ...rest);
  };
  XMLHttpRequest.prototype.abort = function() {
    const state = xhrStates.get(this);
    if (state) state.aborted = true;
    return xhrAbort.call(this);
  };
  XMLHttpRequest.prototype.send = function(body) {
    const state = xhrStates.get(this);
    if (!state || !state.action) return xhrSend.call(this, body);
    const xhr = this;
    function reject(message) {
      const data = failure(message);
      const text = JSON.stringify(data);
      state.synthetic = true;
      Object.defineProperties(xhr, {
        readyState: {configurable: true, value: 4}, status: {configurable: true, value: 422},
        statusText: {configurable: true, value: 'Unprocessable Entity'},
        response: {configurable: true, value: xhr.responseType === 'json' ? data : text},
        responseText: {configurable: true, value: text}
      });
      xhr.dispatchEvent(new Event('readystatechange'));
      xhr.dispatchEvent(new ProgressEvent('load'));
      xhr.dispatchEvent(new ProgressEvent('loadend'));
    }
    // A synchronous cart write cannot run an async safety check.
    if (state.async === false) { reject(messages.error); return; }
    serial(async () => {
      if (state.aborted) return;
      let message;
      try { message = await validate(await requestPayload(state.url, {body}), state.action); }
      catch (_) { message = messages.error; }
      if (state.aborted) return;
      if (message) { reject(message); return; }
      await new Promise(resolve => {
        xhr.addEventListener('loadend', resolve, {once: true});
        xhrSend.call(xhr, body);
      });
      if (xhr.status >= 200 && xhr.status < 300) await afterMutation();
    }).catch(() => { if (!state.aborted) reject(messages.error); });
  };

  async function checkoutAllowed() {
    await mutationQueue;
    try {
      await readCart();
      if (currentMode === 'mixed') { showMessage(messages.mixed); return false; }
      return true;
    } catch (_) { showMessage(messages.error); return false; }
  }

  // Native product forms are gated and then resubmitted with the original
  // submitter, so Optionize handlers and native field validation still run.
  document.addEventListener('submit', async event => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;
    if (allowedForms.has(form)) { allowedForms.delete(form); return; }
    const action = endpoint(form.action);
    const checkout = event.submitter && event.submitter.name === 'checkout';
    if (action !== 'add' && !checkout) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    if (busyForms.has(form)) return;
    busyForms.add(form);
    try {
      let message;
      if (checkout) { if (!await checkoutAllowed()) return; }
      else {
        await mutationQueue;
        message = await validate(parseFields(new FormData(form)), 'add');
        if (message) { showMessage(message); return; }
      }
      allowedForms.add(form);
      form.requestSubmit(event.submitter || undefined);
      allowedForms.delete(form);
    } catch (_) { showMessage(messages.error); }
    finally { busyForms.delete(form); }
  }, true);

  document.addEventListener('click', async event => {
    const target = event.target.closest && event.target.closest('button[name="checkout"], a[href]');
    if (!target) return;
    const isCheckout = target.name === 'checkout' ||
      (target.tagName === 'A' && /\/checkout(?:\/|$)/.test(new URL(target.href).pathname));
    if (!isCheckout) return;
    if (allowedClicks.has(target)) { allowedClicks.delete(target); return; }
    event.preventDefault();
    event.stopImmediatePropagation();
    if (await checkoutAllowed()) {
      allowedClicks.add(target);
      if (target.form) allowedForms.add(target.form);
      target.click();
      if (target.form) allowedForms.delete(target.form);
      allowedClicks.delete(target);
    }
  }, true);

  function start() {
    readCart().catch(() => { currentMode = 'error'; applyCheckoutState(); });
    new MutationObserver(applyCheckoutState).observe(document.body, {childList: true, subtree: true});
    window.addEventListener('focus', () => readCart().catch(() => {}));
    document.addEventListener('visibilitychange', () => {
      if (!document.hidden) readCart().catch(() => {});
    });
  }
  window.AmigosComboCart = {refresh: readCart};
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, {once: true});
  else start();
})();
